import { BO, BusinessObjectBase, FeatureFlagsRulesServiceBase, ModelBase, ModelSerializer, ObservableCollection, PropertyChangedJoinPoint, QueryBus, Weaver } from '@skylab-shared/core';
import { AddressModel, ExchangeRateModel, InventoryEffect, ProductJpkVatCodes, SaleDocumentAdjustmentModel, SaleDocumentItemModel, SaleDocumentKind, SaleDocumentModel, VatSummary } from '@skylab-shared/dataModel';
import { RecursiveDiff } from '@skylab-shared/types';
import { dateDiffInDays, getMinimalOfDates, isNumber, sameValues, substractDays, unique } from '@skylab-shared/utils';
import { ClientRepositoryBase } from '../client/client.repo-base';
import { ContextKeys } from '../common/context-keys';
import { propertyChangedJoinPointFilter } from '../common/property-changed/property-changed-filter';
import { CommunityTransactionForSaleQuery } from '../community-transaction/queries/community-transaction-for-sale.query-base';
import { CompanyParametersQuery } from '../company/queries/company-parameters.query-base';
import { CurrencyCollectionQuery } from '../currency/queries/currency.query-base';
import { DocumentClientAdjustmentValidationBoPart } from '../documents/bo-parts/document-client-adjustment-validation-bo-part';
import { DocumentExchangeRateValidationBoPart } from '../documents/bo-parts/document-exchange-rate-validation.bo-part';
import { DocumentItemsNumberingBoPart } from '../documents/bo-parts/document-items-numbering.bo-part';
import { DocumentNumberBoPart } from '../documents/bo-parts/document-number.bo-part';
import { DocumentVatRateForSaleValidationBoPart } from '../documents/bo-parts/document-vat-rate-for-sale-validation.bo-part';
import { DocumentWstoBoPart } from '../documents/bo-parts/document-wsto.bo-part';
import { CommunityTransactionForSaleMixin } from '../documents/mixins/community-transaction-for-sale.mixin';
import { ExchangeRateMixin } from '../documents/mixins/exchange-rate.mixin';
import { ToPayMixin } from '../documents/mixins/to-pay.mixin';
import { DocumentLinkKind } from '../documents/queries/document-links.query-base';
import { DocumentRecalculatedJoinPoint, documentRecalculatedJoinPointFilter } from '../documents/utils/recalculate-document-join-point';
import { DocumentAdjustmentWrongInventoryEffectError } from '../documents/violations/document-adjustment-wrong-inventory-effect-error';
import { NumberingQuery } from '../numbering/queries/numbering.query-base';
import { PaymentMethodQuery } from '../payment-method/queries/payment-method.query-base';
import { ProductRepositoryContract } from '../shared-business-temporary-contracts';
import { BusinessDayCalculatorPL } from '../utils/business-days-calculator';
import { VatRateForSaleQuery } from '../vat/queries/vat-rate-for-sale.query-base';
import { VatReleaseReasonQuery } from '../vat/queries/vat-release-reason.query-base';
import { BankAccountNumberMixin } from './mixins/bank-account.mixin';
import { ClientMixin } from './mixins/client.mixin';
import { SaleLinksMixin } from './mixins/sale-links.mixin';
import { TaxLiabilityDateMixin } from './mixins/tax-liability-date.mixin';
import { SaleDocumentCalculatorBoPart } from './parts/sale-document-calculator.bo-part';
import { SaleDocumentClientBoPart } from './parts/sale-document-client.bo-part';
import { SaleDocumentIssuedToInventoryGoodsIssueBoPart } from './parts/sale-document-issued-to-inventory-goods-issue.bo-part';
import { SaleDocumentItemsValidationsBoPart } from './parts/sale-document-items-validations.bo-part';
import { SaleDocumentJpkGroupsBoPart } from './parts/sale-document-jpk-groups.bo-part';
import { SaleDocumentKsefBoPart } from './parts/sale-document-ksef.bo-part';
import { SaleDocumentPaymentsBoPart } from './parts/sale-document-payment.bo-part';
import { SaleDocumentReverseChargeAndNPBoPart } from './parts/sale-document-reverse-charge-and-NP.bo-part';
import { SaleDocumentSplitPaymentBoPart } from './parts/sale-document-split-payment.bo-part';
import { SaleDocumentVatReleaseBoPart } from './parts/sale-document-vat-release.bo-part';
import { SaleDocumentWithDueDateBoPart } from './parts/sale-document-with-due-date.bo-part';
import { SaleDocumentPlainObjectQuery } from './queries/sale-document-plain-object.query-base';
import { SaleKindQuery } from './queries/sale-kind.query-base';
import { rewritePlainObjectToDocumentModel } from './utils/sale-rewriting-utils';
import { isDocumentToKsef } from './utils/sale-utils';
import { DateIncludedInAccountingWarning } from './violations/date-included-in-accounting-warning';
import { IssueDateEarlierThanAdjustedError } from './violations/issue-date-earlier-than-adjusted-error';
import { JpkVatCodeShouldBeSelectedOnItemWarning } from './violations/jpk-vat-code-should-be-selected-on-item-warning';
import { SaleAdjustmentShouldNotBeEInvoiceWithAdjustedWstoOss } from './violations/sale-adjustment-should-not-be-e-invoice-with-adjusted-wsto-oss-error';
import { SaleGoodsIssueDateEarlierThanAdjustedError } from './violations/sale-goods-issue-date-earlier-than-adjusted-error';

type OriginalDocumentItemValues = RecursiveDiff<SaleDocumentItemModel, ModelBase>;

export interface SaleDocumentAdjustmentBO extends ClientMixin<SaleDocumentAdjustmentModel>,ExchangeRateMixin<SaleDocumentAdjustmentModel>, CommunityTransactionForSaleMixin<SaleDocumentAdjustmentModel>, ToPayMixin<SaleDocumentAdjustmentModel>, SaleLinksMixin<SaleDocumentAdjustmentModel>, TaxLiabilityDateMixin<SaleDocumentAdjustmentModel>, BankAccountNumberMixin<SaleDocumentAdjustmentModel>{};

@BO({
    modelType: SaleDocumentAdjustmentModel,
    mixins: [ExchangeRateMixin, CommunityTransactionForSaleMixin, ToPayMixin, SaleLinksMixin, TaxLiabilityDateMixin, BankAccountNumberMixin, ClientMixin]
})
export class SaleDocumentAdjustmentBO extends BusinessObjectBase<SaleDocumentAdjustmentModel> {
    public constructor(
        weaver: Weaver,
        protected readonly productRepository: ProductRepositoryContract,
        protected readonly clientRepository: ClientRepositoryBase,
        protected readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase,
        protected readonly queryBus: QueryBus
    ) {
        super(weaver);
        this.initBOParts();
    }
    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.resolve([
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(CurrencyCollectionQuery),
            this.localContext.preload(CommunityTransactionForSaleQuery),
            this.localContext.preload(PaymentMethodQuery),
            this.localContext.preload(SaleKindQuery),
            this.localContext.preload(VatRateForSaleQuery),
            this.localContext.preload(VatReleaseReasonQuery),
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(ContextKeys.isInvoiceToInventoryGoodsIssue(this.model.sun), () => Promise.resolve(!!this.linksInventory))
        ]);

        if (this.model.isPersistent) {
            await this.loadSourceClient();
            await this.loadLinksAsync();

            this.localContext.refresh(ContextKeys.isInvoiceToInventoryGoodsIssue(this.model.sun));

            this.setInfoAboutAdjustedDocument();

            await this.setAdjustedDocumentAsync(this.linksPreviouslyAdjusted?.documentId);
            this.synchronizeProductsJpkGroups();
        }

        this.registerValidationRules();
        this.registerReadonlyRules();
        this.registerCanExecuteRules();
        this.registerPropretyChanged();
    }

    private initBOParts(): void {
        this.parts.push(...[
            new SaleDocumentItemsValidationsBoPart(this.weaver, this.productRepository, this.featureFlagsRulesService, this.queryBus),
            new DocumentVatRateForSaleValidationBoPart(this.weaver),
            new DocumentItemsNumberingBoPart(this.weaver),
            new DocumentClientAdjustmentValidationBoPart(this.weaver, () => this.adjustedDocument),
            new SaleDocumentCalculatorBoPart(this.weaver),
            new SaleDocumentWithDueDateBoPart(this.weaver),
            new SaleDocumentPaymentsBoPart(this.weaver),
            new SaleDocumentClientBoPart(this.weaver, this.featureFlagsRulesService),
            new SaleDocumentSplitPaymentBoPart(this.weaver, this.productRepository, this.featureFlagsRulesService),
            new SaleDocumentReverseChargeAndNPBoPart(this.weaver),
            new DocumentNumberBoPart(this.weaver, this.queryBus),
            new DocumentWstoBoPart(this.weaver, this.queryBus),
            new SaleDocumentJpkGroupsBoPart(this.weaver, this.featureFlagsRulesService),
            new SaleDocumentVatReleaseBoPart(this.weaver, this.queryBus),
            new DocumentExchangeRateValidationBoPart(this.weaver),
            new SaleDocumentKsefBoPart(this.weaver, () => this.adjustedDocument),
            new SaleDocumentIssuedToInventoryGoodsIssueBoPart(this.weaver)
        ]);
    }

    private registerValidationRules(): void {
        this.registerTaxLiabilityDateValidationRules();
        this.registerDatesEarlierThanAdjustedRules();
        this.registerJpkVatGroupShouldBeSynchronizedRule();
        this.registerWrongInventoryEffectOnDocumentValidationRule();
        this.registerDocumentShouldNotBeEInvoiceWithAdjustedWstoOss();
    }

    private registerWrongInventoryEffectOnDocumentValidationRule(): void {
        const inventoryEffectOrder = Object.values(InventoryEffect).filter(isNumber);
        const getInventoryEffectOrder = (inventoryEffect: InventoryEffect | null | undefined) => inventoryEffectOrder.findIndex(elem => elem === inventoryEffect);

        this.validation.addRule(
            [[SaleDocumentAdjustmentModel, 'inventoryEffect']],
            ctx => {
                const previousDocumentInventoryEffectIndex = getInventoryEffectOrder(this.adjustedDocument?.inventoryEffect);
                if (previousDocumentInventoryEffectIndex < 0) return true;

                const currentInventoryEffectIndex = getInventoryEffectOrder(ctx.bo.model.inventoryEffect);
                return currentInventoryEffectIndex <= previousDocumentInventoryEffectIndex;
            },
            DocumentAdjustmentWrongInventoryEffectError
        );
    }

    private registerTaxLiabilityDateValidationRules(): void {
        this.validation.addRule(
            [[SaleDocumentAdjustmentModel, 'taxLiabilityDate']],
            async () => {
                if (this.model.issuedByVatPayer) {
                    return !(await this.isTaxLiabilityDateIncludedInAccountancyAsync());
                }
                return true;
            },
            DateIncludedInAccountingWarning
        );
    }

    private registerDatesEarlierThanAdjustedRules(): void {
        this.validation.addRule(
            [[SaleDocumentAdjustmentModel, 'issueDate']],
            async ctx => {
                if (this.adjustedDocument) {
                    const currentIssueDate = new Date(ctx.source.issueDate);
                    const adjustedIssueDate = new Date(this.adjustedDocument.issueDate);
                    return !(currentIssueDate < adjustedIssueDate);
                }
                return true;
            },
            IssueDateEarlierThanAdjustedError
        );

        this.validation.addRule(
            [[SaleDocumentAdjustmentModel, 'goodsIssueDate']],
            _ => {
                if (!this.adjustedDocument) return true;
                if (!this.adjustedDocument.inventoryEffect || !this.model.inventoryEffect) return true;

                const prevDateToCompare = this.adjustedDocument.goodsIssueDate || this.adjustedDocument.saleDate;
                const currDateToCompare = this.model.goodsIssueDate || this.model.saleDate;

                return !(currDateToCompare < prevDateToCompare);
            },
            SaleGoodsIssueDateEarlierThanAdjustedError
        );
    }

    private registerDocumentShouldNotBeEInvoiceWithAdjustedWstoOss(): void {
        this.validation.addRule(
            [[SaleDocumentAdjustmentModel, 'ksefSubmissionMode']],
            () => {
                if (!isDocumentToKsef(this.model)) return true;
                return !this.isWstoOssAdjustedDocument;
            },
            SaleAdjustmentShouldNotBeEInvoiceWithAdjustedWstoOss
        );
    }

    private registerJpkVatGroupShouldBeSynchronizedRule(): void {
        this.validation.addRule(
            [[SaleDocumentAdjustmentModel, 'jpkVatGroups']],
            [[SaleDocumentAdjustmentModel, 'jpkVatGroups']],
            () => {
                const itemsJpkCodes = unique([...this.itemsChangeIndexes].map(index => this.model.items[index]?.jpkVatGroup).filter(elem => elem).sort());
                const modelJpkCodes = this.model.jpkVatGroups.filter(elem => Object.values(ProductJpkVatCodes).includes(elem as ProductJpkVatCodes)).sort();
                return (itemsJpkCodes.length === modelJpkCodes.length) && sameValues(itemsJpkCodes, modelJpkCodes);
            },
            JpkVatCodeShouldBeSelectedOnItemWarning
        );
    }

    private registerReadonlyRules(): void {
        this.readonly.setAsReadonly([SaleDocumentAdjustmentModel, 'kind']);
        this.readonly.setAsReadonly([SaleDocumentAdjustmentModel, 'calculationMethod']);
        this.readonly.setAsReadonly([SaleDocumentAdjustmentModel, 'currencyId']);
        this.readonly.setAsReadonly([SaleDocumentAdjustmentModel, 'marginSchemeId']);

        this.readonly.setAsReadonly([SaleDocumentItemModel, 'cost']);
        this.readonly.setAsReadonly([SaleDocumentItemModel, 'unitCost']);

        this.readonly.setAsReadonly([SaleDocumentAdjustmentModel, 'orderNumber']);
        this.readonly.setAsReadonly([SaleDocumentAdjustmentModel, 'orderDate']);
        this.readonly.setAsReadonly([SaleDocumentAdjustmentModel, 'agreementNumber']);
        this.readonly.setAsReadonly([SaleDocumentAdjustmentModel, 'agreementDate']);

        this.readonly.addRule(
            [[SaleDocumentAdjustmentModel, 'kind']],
            [[SaleDocumentAdjustmentModel, 'communityTransactionId']],
            ({ source: { kind } }) => kind === SaleDocumentKind.ReceiptAdjustment || kind === SaleDocumentKind.VatMarginInvoiceAdjustment
        );

        this.readonly.addRule([
            [SaleDocumentAdjustmentModel, 'communityTransactionId'],
            [SaleDocumentAdjustmentModel, 'transactionLocation'],
            [SaleDocumentAdjustmentModel, 'issueDate'],
            [SaleDocumentAdjustmentModel, 'goodsIssueDate'],
            [SaleDocumentAdjustmentModel, 'saleDate'],
            [SaleDocumentAdjustmentModel, 'dueDate'],
            [SaleDocumentAdjustmentModel, 'dueDateInDays'],
            [SaleDocumentAdjustmentModel, 'paymentMethodId'],
            [SaleDocumentAdjustmentModel, 'bankAccountNumberId'],
            [SaleDocumentAdjustmentModel, 'baseCurrencyBankAccountNumberId'],
            [SaleDocumentAdjustmentModel, 'issuer'],
            [SaleDocumentAdjustmentModel, 'recipient'],
            [SaleDocumentAdjustmentModel, 'client'],
            [SaleDocumentAdjustmentModel, 'buyer'],
            [SaleDocumentAdjustmentModel, 'marginSchemeId'],
            [SaleDocumentAdjustmentModel, 'adjustmentReasonId'],
            [SaleDocumentAdjustmentModel, 'exchangeRate'],
            [SaleDocumentAdjustmentModel, 'jpkVatGroups'],
            [SaleDocumentAdjustmentModel, 'splitPayment'],
            [SaleDocumentAdjustmentModel, 'customDueDateDescription'],
            [SaleDocumentAdjustmentModel, 'wstoConsumerCountryId'],
            [SaleDocumentAdjustmentModel, 'wstoShipmentCountryId'],
            [SaleDocumentAdjustmentModel, 'numberingId'],
            [SaleDocumentAdjustmentModel, 'issuedByVatPayer'],
            [SaleDocumentAdjustmentModel, 'ksefSubmissionMode'],
            [SaleDocumentAdjustmentModel, 'vatReleaseReasonId'],
            [SaleDocumentAdjustmentModel, 'vatReleaseReasonCustomName'],
            [SaleDocumentAdjustmentModel, 'vatReleaseReasonSource'],
            [SaleDocumentAdjustmentModel, 'rateNP'],
            [SaleDocumentAdjustmentModel, 'reverseCharge'],
            [SaleDocumentAdjustmentModel, 'adjustPaymentDate'],
        ], () => this.linksAdjusted || this.linksReceiptWithIssuedInvoice);

        this.readonly.addRule(
            [[SaleDocumentItemModel, 'productId']],
            [[SaleDocumentItemModel, 'productName'], [SaleDocumentItemModel, 'unitId']],
            ctx => ctx.source.includesInventoryControl && !!this.adjustedDocument?.relatedInventoryDocuments.length && !!this.adjustedItems?.find(elem => elem.ordinalNumber === ctx.source.ordinalNumber
        ));

        this.readonly.setReadonlyOnSubtree(SaleDocumentItemModel, () => this.linksAdjusted || this.linksReceiptWithIssuedInvoice, {
            exceptions: [
                [SaleDocumentItemModel, <keyof SaleDocumentItemModel>'note']
            ]
        });

        this.readonly.setReadonlyOnSubtree(ExchangeRateModel, () => this.linksAdjusted || this.linksReceiptWithIssuedInvoice);
        this.readonly.setReadonlyOnSubtree(AddressModel, () => this.linksAdjusted || this.linksReceiptWithIssuedInvoice);

        this.readonlyChecker.addOmitRule([
            [SaleDocumentItemModel, 'cost'],
            [SaleDocumentItemModel, 'unitCost']
        ], () => (this.model.kind === SaleDocumentKind.VatMarginInvoiceAdjustment));
    }

    public registerCanExecuteRules(): void {
        this.canExecute.addRule(
            [[ObservableCollection, 'add'], [ObservableCollection, 'remove']],
            ctx => ctx.target === ctx.bo.model.items
                ? !(this.linksAdjusted || this.linksReceiptWithIssuedInvoice)
                : true
        );
    }

    private registerPropretyChanged(): void {
        this.updateBankAccountsNumbers();
        this.registerCostRuleAfterRateChange();
        this.updateGrossValueChanged();
    }

    private updateBankAccountsNumbers(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SaleDocumentModel, ['currencyId', 'communityTransactionId', 'paymentMethodId'])(
            _ => this.updateBankAccountNumber()
        )));
    }
   
    private updateGrossValueChanged(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SaleDocumentAdjustmentModel, ['grossValue'])(
            _ => {
                if(this.model.grossValue !== 0) {
                    this.model.adjustPaymentDate = false;
                } 
            }
        )));
    }

    private registerCostRuleAfterRateChange() {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(ExchangeRateModel, ['rate'])(
            () => this.validation.queueValidation([SaleDocumentItemModel, 'price_grossBeforeDiscount'])
        )));
    }

    public runJpkVatGroupItemsSynchronizer(): void {
        this.registerListening(this.weaver.register(DocumentRecalculatedJoinPoint, documentRecalculatedJoinPointFilter(this)()(
            () => this.synchronizeProductsJpkGroups()
        )));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SaleDocumentItemModel, ['jpkVatGroups'])(
            () => this.synchronizeProductsJpkGroups()
        )));
    }

    private itemsChangeIndexes = new Set<number>();
    private synchronizeProductsJpkGroups(): void {
        const props = <(keyof SaleDocumentItemModel)[]>['value_grossBeforeDiscount', 'discountValue', 'quantity', 'price_grossBeforeDiscount', 'vatRateId', 'jpkVatGroup'];
        const toAdd: any[] = []; let toRemove: any[] = [];

        const isItemChanged = (current: any, original: any) => props.reduce((pV, key) => {
            if (key === 'jpkVatGroup') {
                return current[key] !== original.jpkVatGroups?.[0] || pV;
            }
            return current[key] !== original[key] || pV;
        }, false);

        this.model.items.forEach((elem, index) => {
            if (this.adjustedItems[index] && !isItemChanged(elem, this.adjustedItems[index])) {
                toRemove.push(...elem.jpkVatGroups);
                this.itemsChangeIndexes.delete(index);
            } else {
                toAdd.push(...elem.jpkVatGroups);
                this.itemsChangeIndexes.add(index);
            }
        });

        toRemove = toRemove.filter(elem => !toAdd.includes(elem));
        const current = this.model.jpkVatGroups.filter(elem => !toRemove.includes(elem));

        this.model.jpkVatGroups = unique([...current, ...toAdd]);
    }

    protected calculateDateForCurrencyExchangeRate(): Date {
        const date = this.model.issuedByVatPayer
            ? getMinimalOfDates(this.model.issueDate, this.model.taxLiabilityDate)
            : dateDiffInDays(this.model.issueDate, this.model.saleDate) < 0 ? this.model.issueDate : this.model.saleDate;

        return BusinessDayCalculatorPL.getLastBusinessDay(substractDays(date!, 1));
    }

    private internalVatSummary!: VatSummary;
    public get vatSummary(): VatSummary {
        return this.internalVatSummary;
    }

    private adjustedDocument!: SaleDocumentModel | SaleDocumentAdjustmentModel;
    private adjustedItems!: OriginalDocumentItemValues[];
    private keepOriginalItemsValues(): void {
        this.adjustedItems = this.adjustedDocument.items.map(item => ModelSerializer.toPlainObject(item));
    }
    public async setAdjustedDocumentAsync(adjustedDocumentId?: number): Promise<(SaleDocumentModel | SaleDocumentAdjustmentModel) | undefined> {
        if (!adjustedDocumentId) {
            throw Error('Adjusted document id is not defined');
        }
        this.adjustedDocument = await this.localContext.getAsync(ContextKeys.document(adjustedDocumentId), async () => {
            const plainObject = await this.queryBus.queryAsync(new SaleDocumentPlainObjectQuery(), adjustedDocumentId);
            return rewritePlainObjectToDocumentModel(plainObject);
        });
        this.keepOriginalItemsValues();

        return this.adjustedDocument;
    }

    public get isWstoOssAdjustedDocument(): boolean {
        const { id: wstoId } = this.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { symbol: 'WSTO-OSS' })!;
        return wstoId === this.adjustedDocument?.communityTransactionId;
    }

    private setInfoAboutAdjustedDocument(): void {
        if (this.links.adjustments) {
            const originalDocument = this.links.adjustments.filter(a => a.role === DocumentLinkKind.Original)[0];
            this.model.originalDocumentId = originalDocument.documentId;
            this.model.originalDocumentVersion = originalDocument.version;
        }
    }

}
